// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

/**
 * SeatChip (#64) — the header seat chip ("Demo Seat", "Educational Seat"),
 * driven by one `SEAT_CHIPS` entry (`./seat-chips`). Display-only: it is fed
 * by untrusted query-string flags (see the Trust rule in `./seat-chips`).
 *
 * NC COMPONENT CANDIDATE: AU0001 and AU0008 need the same chip (AU0008 #79
 * already has one). It is built locally from tokens and is NOT promoted to an
 * NC component here (NCDS Core Rule 5). `primitives/badge.tsx` is not reused:
 * it has no outline pill style (rounded-md, tinted fill, tone-coloured text).
 *
 * Composed into `NCPageHeader`'s AU-specific `titleAdornment` slot,
 * immediately right of the title. The header renders on every view (search,
 * query progress, device view, halt report), so the chip does too; it wraps
 * with the title on narrow widths.
 *
 *  - Pill (`--radius-full`), 24 px high, 10 px horizontal padding, 6 px gap,
 *    surface background (`--nc-bg-surface`), `--border-width-1` border.
 *  - Tone: only the border and the icon take the tone colour — warning
 *    (`--color-nc-warning`) for Demo, secondary (`--nc-color-secondary-1`)
 *    for Educational. Both are ~3.2–3.5:1 on white: enough for non-text UI,
 *    not for text, so the text is `--nc-text-primary` in both variants.
 *  - The variants differ in text and icon, not only in colour.
 *  - Not dismissible.
 *  - Tooltip (#67, parity with AU0008 #79): when the spec has a `tooltip`,
 *    the pill is focusable (`tabIndex={0}`, visible focus ring) and opens a
 *    short supplementary tooltip (`primitives/tooltip`) on hover, on keyboard
 *    focus, and on tap or click; Escape or a press elsewhere closes it. The
 *    label alone still carries the full meaning and hover is only one of
 *    three ways to open it, so nothing depends on hover. The content is
 *    portalled outside the `role="status"` region, so it is never part of
 *    the chip's one announcement.
 *  - Accessibility: the `role="status"` region is mounted empty and the pill
 *    is inserted into it once, in a layout effect — after the region is in
 *    the document (so assistive tech observes the change and announces it
 *    once) but before the first paint (so there is no visible shift). Its
 *    text never changes afterwards.
 */

import { useLayoutEffect, useState } from "react";

import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip";

import {
  SEAT_CHIPS,
  type SeatChipIcon,
  type SeatChipKey,
  type SeatChipSpec,
  type SeatChipTone,
} from "./seat-chips";

/** Border and icon colour per tone; the text colour never follows the tone. */
const TONE_CLASSES: Readonly<Record<SeatChipTone, { border: string; icon: string }>> = {
  warning: { border: "border-nc-warning", icon: "text-nc-warning" },
  secondary: { border: "border-[var(--nc-color-secondary-1)]", icon: "text-[var(--nc-color-secondary-1)]" },
};

/**
 * The chip icons, both drawn for #64 / AU0008 #79 (not taken from an icon
 * set, so no third-party licence applies). The stroke follows `currentColor`,
 * set from the tone token in CSS.
 *  - office-chair: backrest, connector, seat, gas-lift column, base, three
 *    casters.
 *  - graduation-cap: board, cap body, tassel.
 */
function SeatIcon({ icon, className }: { icon: SeatChipIcon; className: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn("shrink-0", className)}
    >
      {icon === "office-chair" ? (
        <>
          <rect x="7" y="2" width="10" height="8" rx="3" />
          <line x1="12" y1="10" x2="12" y2="12" />
          <rect x="5" y="12" width="14" height="3" rx="1.5" />
          <line x1="12" y1="15" x2="12" y2="19" />
          <path d="M6 19h12" />
          <circle cx="6" cy="21" r="1" />
          <circle cx="12" cy="21" r="1" />
          <circle cx="18" cy="21" r="1" />
        </>
      ) : (
        <>
          <path d="M2 9l10-5 10 5-10 5z" />
          <path d="M6 11v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5" />
          <line x1="22" y1="9" x2="22" y2="15" />
        </>
      )}
    </svg>
  );
}

// Font size as `text-[length:…]`, not `text-nc-1`: tailwind-merge (`cn`) does
// not know `text-nc-*` and drops it as a conflicting text colour next to
// `text-[var(--nc-text-primary)]`, leaving the inherited size.
const PILL_CLASSES =
  "inline-flex h-6 items-center gap-1.5 rounded-full border-[length:var(--border-width-1)] bg-[var(--nc-bg-surface)] px-2.5 text-[length:var(--text-1)] font-nc-2 leading-nc-1 whitespace-nowrap text-[var(--nc-text-primary)]";

/**
 * The pill itself, for one `SEAT_CHIPS` entry: plain without a `tooltip`,
 * otherwise the tooltip trigger (mirrors AU0008's `SeatPill`).
 */
export function SeatPill({ chipKey }: { chipKey: SeatChipKey }) {
  const chip: SeatChipSpec = SEAT_CHIPS[chipKey];
  const [open, setOpen] = useState(false);
  const tone = TONE_CLASSES[chip.tone];
  const body = (
    <>
      <SeatIcon icon={chip.icon} className={tone.icon} />
      {chip.label}
    </>
  );
  if (chip.tooltip === undefined) {
    return <span className={cn(PILL_CLASSES, tone.border)}>{body}</span>;
  }
  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          // A tap or click toggles it. preventDefault keeps Radix from closing
          // the tooltip on the same press, which it does by default.
          onPointerDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.preventDefault();
            setOpen((wasOpen) => !wasOpen);
          }}
          className={cn(
            PILL_CLASSES,
            tone.border,
            "cursor-default outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          )}
        >
          {body}
        </span>
      </TooltipTrigger>
      <TooltipContent>{chip.tooltip}</TooltipContent>
    </Tooltip>
  );
}

export function SeatChip({ chipKey }: { chipKey: SeatChipKey }) {
  const [inserted, setInserted] = useState(false);
  useLayoutEffect(() => {
    setInserted(true);
  }, []);
  return (
    <span role="status" className="inline-flex">
      {inserted ? <SeatPill chipKey={chipKey} /> : null}
    </span>
  );
}
